import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type NotificacaoPainel, notificacaoConflitoDetalhesSchema } from '@varga/shared';
import { type ReactNode, useState } from 'react';
import { listarNotificacoes, marcarEnviada } from '../api/notificacoes';
import { useToast } from '../components/toast';
import { CabecalhoTela } from '../components/Shell';
import { BlocoAviso, Botao, Chip, EstadoVazio, LinhasEsqueleto } from '../components/ui';
import { ApiError } from '../lib/http';

const ROTULO_TIPO: Record<string, string> = {
  boas_vindas: 'Boas-vindas',
  indicacao_convertida: 'Indicação convertida',
  recuperacao_senha: 'Recuperação de senha',
  quase_la: 'Quase lá',
};

export function NotificacoesPagina(): ReactNode {
  const qc = useQueryClient();
  const toast = useToast();
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const [saindo, setSaindo] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ['notificacoes', 'pendente'],
    queryFn: () => listarNotificacoes('pendente'),
  });

  const mut = useMutation({
    mutationFn: (id: string) => marcarEnviada(id),
    onSuccess: () => {
      toast('Marcada como enviada.');
      void qc.invalidateQueries({ queryKey: ['notificacoes'] });
    },
    onError: (err, id) => {
      if (err instanceof ApiError && err.codigo === 'conflito') {
        const det = notificacaoConflitoDetalhesSchema.safeParse(err.detalhes);
        const quem = det.success && det.data.enviadaPor ? det.data.enviadaPor : 'Outro operador';
        setSaindo((s) => new Set(s).add(id));
        toast(`${quem} já enviou esta notificação.`, 'erro');
        window.setTimeout(() => void qc.invalidateQueries({ queryKey: ['notificacoes'] }), 400);
      } else {
        toast('Não foi possível marcar agora. Tente de novo.', 'erro');
      }
    },
  });

  return (
    <div>
      <CabecalhoTela titulo="Notificações" />

      {q.isLoading && <LinhasEsqueleto linhas={5} colunas={3} />}
      {q.isError && (
        <BlocoAviso tom="aviso" aoTentarDeNovo={() => void q.refetch()}>
          Não foi possível carregar a fila.
        </BlocoAviso>
      )}
      {q.data && q.data.length === 0 && (
        <EstadoVazio titulo="Tudo em dia. Nenhuma notificação pendente." />
      )}

      {q.data && q.data.length > 0 && (
        <ul className="flex flex-col">
          {q.data.map((n) => (
            <li
              key={n.id}
              className={
                'border-b border-linha py-3 transition-opacity ' +
                (saindo.has(n.id) ? 'opacity-0' : 'opacity-100')
              }
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-tinta-forte">{n.clientePrimeiroNome}</span>
                <Chip>{ROTULO_TIPO[n.tipo] ?? n.tipo}</Chip>
                <span className="text-nota text-tinta-2">{n.origem.descricao}</span>
                <TempoRelativo iso={n.criadoEm} />
              </div>
              <p className="mt-1 text-corpo text-tinta">{instrucao(n)}</p>
              <div className="mt-2 flex gap-2">
                <a
                  href={n.linkWa}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setAbertas((a) => new Set(a).add(n.id))}
                  className="inline-flex h-8 items-center rounded-1 border border-linha-forte bg-superficie-alta px-3 font-display text-[0.8125rem] font-bold text-tinta-forte"
                >
                  Abrir no WhatsApp
                </a>
                <Botao
                  variante={abertas.has(n.id) ? 'primario' : 'secundario'}
                  className="h-8 px-3 text-[0.8125rem]"
                  carregando={mut.isPending && mut.variables === n.id}
                  onClick={() => mut.mutate(n.id)}
                >
                  Marcar como enviada
                </Botao>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function instrucao(n: NotificacaoPainel): string {
  if (n.tipo === 'boas_vindas')
    return `Enviar o código de indicação para ${n.clientePrimeiroNome}.`;
  if (n.tipo === 'indicacao_convertida') {
    const indicador = n.origem.indicadorPrimeiroNome ?? n.clientePrimeiroNome;
    const indicado = n.origem.indicadoPrimeiroNome ?? 'a pessoa indicada';
    return `Avisar ${indicador} que a indicação de ${indicado} virou brinde.`;
  }
  return n.texto.split('\n')[0] ?? n.texto;
}

function TempoRelativo({ iso }: { iso: string }): ReactNode {
  const ms = Date.now() - new Date(iso).getTime();
  const horas = ms / 3_600_000;
  const texto =
    horas < 1
      ? 'há poucos minutos'
      : horas < 48
        ? `há ${Math.round(horas)} h`
        : `há ${Math.round(horas / 24)} dias`;
  return (
    <span className={'text-nota ' + (horas >= 48 ? 'text-aviso' : 'text-tinta-3')}>
      Criada {texto}
    </span>
  );
}
